import { useState, useEffect, CSSProperties } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import {
  Box,
  Grid,
  Typography,
  Link,
  Stack,
  Modal,
  CircularProgress,
} from '@mui/material';
import { saveAs } from 'file-saver';
import { StyledButton } from '@/components/styled/button';
import { FormPublicFieldFragment } from 'graphql/fragment/form.public.fragment';
import MenuBookTwoToneIcon from '@mui/icons-material/MenuBookTwoTone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { lighten } from 'polished';
import html2canvas from 'html2canvas';
import ContactUs from './components/ContactUs';
import {
  generateAnswersTable,
  generateTableLegend,
} from './components/ResultsTable';

interface UserAnswer {
  field: string; // question id
  data: string; // user chosen question answer
}

const PillarsOfProductShape = ({
  scores,
  design,
  jsonData,
  questions,
  userAnswers,
}: any) => {
  ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    {
      id: 'custom_canvas_background_color',
      beforeDraw: (chart) => {
        const ctx = chart.canvas.getContext('2d');
        ctx!.save();
        ctx!.globalCompositeOperation = 'destination-over';
        ctx!.fillStyle = '#000000';
        ctx!.fillRect(0, 0, chart.width, chart.height);
        ctx!.restore();
      },
    }
  );

  const [saving, setSaving] = useState(false);

  const saveResults = async () => {
    const endPage = document.getElementById('endpage-div');
    const canvas = await html2canvas(endPage!);
    canvas.toBlob(function (blob) {
      saveAs(blob!, 'pillars-of-product-chart.png');
    });
  };

  // used to show saving modal for a second, without it modal just flashes in and out
  const stopSaving = async () => {
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    await sleep(1000);
    setSaving(false);
  };

  useEffect(() => {
    if (saving) {
      saveResults();
      stopSaving();
    }
  }, [saving]);

  const modalStyling: CSSProperties = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    border: `2px solid ${design.colors.question}`,
    borderRadius: '8px',
    backgroundColor: design.colors.background,
    color: design.colors.button,
    padding: '40px',
  };

  const saveChart = () => {
    //save to png
    const canvasSave = document.getElementById('pillars-of-product-chart');
    // @ts-ignore: Object is possibly 'null'.
    canvasSave.toBlob(function (blob) {
      saveAs(blob, 'pillars-of-product-chart.png');
    });
  };

  // console.log(chart.config.options) to see this object
  const chartData = {
    labels: jsonData.chartCategories,
    datasets: [
      {
        label: 'Shape',
        data: scores,
        backgroundColor: 'rgba(134, 188, 37, 0.2)',
        borderColor: 'rgba(134, 188, 37, 1)',
        color: 'rgba(255, 255, 255, 0.75)',
        borderWidth: 1,
      },
    ],
  };

  // console.log(chart.config.options) to see this object
  const chartOptions: {
    plugins: {
      legend: {
        display: boolean;
      };
    };
    scales: {
      r: {
        min: number;
        max: number;
        ticks: {
          stepSize: number;
          callback: any;
        };
        grid: {
          color: string;
          lineWidth: number;
        };
        pointLabels: any;
      };
    };
  } = {
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      r: {
        min: 0,
        max: 3,
        ticks: {
          stepSize: 1,
          callback: function (index: number): string {
            switch (index) {
              case 1:
                return 'Lagging';
              case 2:
                return 'Sustainable';
              case 3:
                return 'Leading';
              default:
                return '';
            }
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.75)',
          lineWidth: 2,
        },
        pointLabels: {
          /* Adjust data label font size according to chart size */
          font: function (context: { chart: { width: any } }) {
            var width = context.chart.width;
            var size = Math.round(width / 48);
            return {
              size: size,
            };
          },
          color: 'rgba(255, 255, 255, 1)',
        },
      },
    },
  };

  if (userAnswers.length !== 21) {
    return (
      <div style={{ height: '100vh', color: 'white' }}>
        Something is wrong with the submission. The amount of answers don&apos;t
        match the amount of questions.
      </div>
    );
  }

  return (
    <div
      className='main-end-page-wrapper'
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <Modal
        open={saving}
        aria-labelledby='saving-modal'
        aria-describedby='appears when results are being saved'
      >
        <Box sx={modalStyling}>
          <CircularProgress color='inherit' />
        </Box>
      </Modal>
      <div
        style={{ backgroundColor: design.colors.background }}
        id='endpage-div'
      >
        <Grid
          container
          justifyContent='center'
          alignItems='center'
          sx={{
            marginTop: '15vh',
            padding: ['0 5%', '0 5%', '0 5%', '0 5%', '0 10%'],
          }}
        >
          <Grid item xs={12}>
            <Typography
              variant='h4'
              align='center'
              color={design.colors.question}
            >
              Thank you for completing your Product Capability Assessment,{' '}
              {userAnswers[0].data.replace(/\"/g, '')}.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              align='center'
              color={design.colors.question}
              sx={{ margin: '5vh 0', whiteSpace: 'pre-line' }}
            >
              Thank you for taking the time to complete our survey on the 4
              pillars of product-centric organizational design. Your inputs have
              been scored across Deloitte&apos;s 16 dimensions of product centricity
              and ranked as Lagging, Leading, or Sustaining.
              {'\n\n'}This scoring is focused on delivering exceptional products
              and services to your customers. We appreciate your willingness to
              share your insights and experiences with us, and to learn more
              about Deloitte&apos;s 4 Pillars of Product Management please read our
              white paper{' '}
              {saving ? null : (
                <Link
                  href='https://www.deloittedigital.com/content/dam/deloittedigital/us/documents/offerings/offering-20210714-pillars-of-product-management.pdf'
                  target='_blank'
                  rel='noreferrer'
                  underline='none'
                  sx={{
                    color: design.colors.button,
                    transition: `color 0.25s ease-in`,
                    ':hover': { color: lighten(0.1, design.colors.button) },
                  }}
                >
                  here
                  <OpenInNewIcon
                    sx={{
                      position: 'relative',
                      top: '3px',
                    }}
                    fontSize='small'
                  />
                </Link>
              )}
              {'.'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant='h4'
              align='center'
              sx={{ color: 'white', padding: '20px', marginBottom: '20px' }}
            >
              Product Capability Assessment Details
            </Typography>
          </Grid>
          {generateAnswersTable(
            questions,
            [
              'Vision &\nStrategy',
              'Talent &\nOrganization',
              'Focus on Users &\nOutcomes',
              'Delivery &\nMindset',
            ],
            userAnswers,
            window.innerWidth <= 900
          )}
          <Grid item xs={12} sx={{ margin: '40px 0' }}>
            <Typography variant='h4' color='white' align='center'>
              Scoring is defined as follows:
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {generateTableLegend([
              'Current operations fall below outlined standards compared to market competitors and require material structural adjustments.',
              'Current operations fall below outlined standards compared to market competitors and require material structural adjustments.',
              'Current operations exceed outlined standards compared to market competitors and differentiate from competition.​',
            ])}
          </Grid>
          <Grid item xs={12} md={4} sx={{ marginTop: ['40px', '40px', '0'] }}>
            <Typography
              variant='h5'
              align='center'
              color={design.colors.question}
            >
              Your next steps
            </Typography>
            <Grid container justifyContent='left' sx={{ marginTop: '20px' }}>
              <Grid
                item
                xs={12}
                md={2}
                alignSelf='center'
                sx={{ textAlign: ['center', 'center', 'left'] }}
              >
                <MenuBookTwoToneIcon sx={{ color: 'white' }} fontSize='large' />
              </Grid>
              <Grid item xs={12} md={10}>
                <Typography
                  color={design.colors.question}
                  sx={{
                    marginBottom: '5%',
                    fontSize: '0.9rem',
                    textAlign: ['center', 'center', 'left'],
                  }}
                >
                  Now that you have shared your insights and experiences with
                  us, we invite you to take the next step and join us in our
                  efforts to create more product-centric organizations. Please
                  reach out to learn more about how Deloitte is leading
                  organizational changes to become more product-centric.
                </Typography>
              </Grid>
              <Grid item xs={2}></Grid>
              <Grid item xs={8}>
                {saving ? null : (
                  <Stack sx={{ alignItems: 'center' }}>
                    <StyledButton
                      background={design.colors.button}
                      $buttonColor={design.colors.buttonText}
                      highlight={design.colors.buttonActive}
                      className={'save-button'}
                      href={'mailto:vattonito@deloitte.com'}
                      style={{
                        margin: '2vh 0',
                        fontWeight: 'bold',
                        width: '75%',
                      }}
                    >
                      Contact
                    </StyledButton>
                    <StyledButton
                      background={design.colors.button}
                      $buttonColor={design.colors.buttonText}
                      highlight={design.colors.buttonActive}
                      className={'save-button'}
                      onClick={() => setSaving(true)}
                      style={{
                        fontWeight: 'bold',
                        width: '75%',
                      }}
                      disableRipple
                    >
                      SAVE YOUR RESULTS
                    </StyledButton>
                  </Stack>
                )}
              </Grid>
              <Grid item xs={2}></Grid>
            </Grid>
          </Grid>
          <Grid item xs={0} md={1}></Grid>
          <Grid item xs={12} md={7}>
            <Box>
              <Radar
                id='pillars-of-product-chart'
                data={chartData}
                options={chartOptions}
              />
            </Box>
          </Grid>
          <ContactUs design={design} />
        </Grid>
      </div>
    </div>
  );
};

export default PillarsOfProductShape;
